import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {AbortController | null} */
  #controller = null;

  #isInsideCartDrawer() {
    return this.closest('cart-drawer-component') instanceof HTMLElement;
  }

  #getCartError() {
    return /** @type {import('./cart-error').CartError | null} */ (
      document.querySelector('cart-error')
    );
  }

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener(
      ThemeEvents.cartUpdate,
      /** @type {EventListener} */ (this.#handleCartUpdate),
      { signal },
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      /** @type {EventListener} */ (/** @type {unknown} */ (this.#debouncedOnChange)),
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {import('@theme/events').QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (lineItemRow) this.shimmerPrices(lineItemRow);
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key,
      ),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Triggers shimmer on cart total and optionally on a specific row's price.
   * @param {HTMLTableRowElement} [row]
   */
  shimmerPrices(row) {
    /** @type {any} */ (this.refs.cartTotal)?.shimmer();
    row?.querySelectorAll('text-component').forEach((el) => {
      if (!el.closest('cart-variant')) /** @type {any} */ (el).shimmer();
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity } = config;

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(this.#sectionsToUpdate()).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          return this.#handleCartError(parsedResponseText);
        }

        this.#getCartError()?.dismissError();

        const sectionId = this.sectionId;
        const sectionHTML = parsedResponseText.sections?.[sectionId];

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, sectionId, {
            itemCount: this.#parseItemCount(
              typeof sectionHTML === 'string'
                ? new DOMParser().parseFromString(sectionHTML, 'text/html')
                : null,
            ),
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          }),
        );

        if (typeof sectionHTML === 'string') {
          this.#applySection(sectionId, sectionHTML);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = async (parsedResponseText) => {
    const errorMessage = parsedResponseText.errors;

    try {
      const url = `${window.location.pathname}?sections=${Array.from(this.#sectionsToUpdate()).join(',')}`;
      const response = await fetch(url);
      const sections = await response.json();

      const sectionId = this.sectionId;
      const sectionHTML = sections?.[sectionId];

      this.dispatchEvent(
        new CartUpdateEvent({}, sectionId, {
          itemCount: this.#parseItemCount(
            typeof sectionHTML === 'string'
              ? new DOMParser().parseFromString(sectionHTML, 'text/html')
              : null,
          ),
          source: 'cart-items-component',
          sections,
        }),
      );

      if (typeof sectionHTML === 'string') {
        this.#applySection(sectionId, sectionHTML);
      }
    } catch (err) {
      console.error(err);
    }

    this.#getCartError()?.showError(errorMessage);
  };

  /**
   * Handles the cart update.
   *
   * @param {CartUpdateEvent | import('@theme/events').CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const sectionId = this.sectionId;
    const cartItemsHtml = event.detail?.data?.sections?.[sectionId];
    if (typeof cartItemsHtml === 'string') {
      this.#applySection(sectionId, cartItemsHtml);
    } else if (!this.#isInsideCartDrawer()) {
      sectionRenderer.renderSection(sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`,
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  #sectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return sectionsToUpdate;
  }

  /** @param {Document | null} doc */
  #parseItemCount(doc) {
    const hidden = doc?.querySelector('[ref="cartItemCount"]')?.textContent;
    return hidden ? parseInt(hidden, 10) : 0;
  }

  /**
   * @param {string} sectionId
   * @param {string} sectionHTML
   */
  #applySection(sectionId, sectionHTML) {
    if (this.#isInsideCartDrawer()) {
      const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const newCartItemsComponent =
        doc.querySelector('cart-drawer-component cart-items-component') ??
        doc.querySelector('cart-items-component');
      if (newCartItemsComponent instanceof HTMLElement) morph(this, newCartItemsComponent);
    } else {
      morphSection(sectionId, sectionHTML);
    }
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
